<template>
  <div class="relative flex size-full flex-col py-8 bg-overlay">
    <UiButton
      v-if="value.length > 1"
      icon
      class="prev-gallery-full group !absolute left-6 z-10 hidden size-12 min-w-12 items-center justify-center !border-none !py-0 inset-y-center lg:flex"
    >
      <div
        class="absolute inset-0 size-full rounded-full opacity-50 transition-all group-hover:opacity-100 group-focus:opacity-100"
      />
      <UiIcon
        name="chevron-down"
        class="size-4 rotate-90 text-icon-secondary transition-transform group-hover:-translate-x-px"
      />
    </UiButton>

    <div
      class="absolute -inset-1/2 flex size-[200%] items-center justify-center blur-[65px] lg:inset-[-30%] lg:size-[160%]"
    >
      <div class="absolute inset-0 size-full bg-primary" />
    </div>

    <UiSwiper>
      <Swiper
        ref="swiper"
        :navigation="{
          prevEl: '.prev-gallery-full',
          nextEl: '.next-gallery-full'
        }"
        :modules="[Navigation, Pagination, Keyboard]"
        :keyboard="{
          enabled: true
        }"
        :pagination="{
          clickable: true,
          bulletElement: 'button',
          el: '.gallery-full-pagination',
          dynamicBullets: value.length > 2
        }"
        :initial-slide="initialSlide"
        centered-slides
        watch-overflow
        :loop="value.length > 2"
        class="my-auto max-h-full w-full"
      >
        <SwiperSlide
          v-for="media in value"
          :key="media.object.id"
          class="flex h-full max-h-screen justify-center px-4"
        >
          <div class="flex h-full flex-col items-center justify-center">
            <UiPicture
              :src="media.object.url"
              class="max-h-screen overflow-hidden rounded-xl object-contain"
            />
          </div>
        </SwiperSlide>
      </Swiper>
    </UiSwiper>
    <UiButton
      v-if="value.length > 1"
      icon
      class="next-gallery-full group !absolute right-6 z-10 hidden size-12 min-w-12 items-center justify-center !border-none !py-0 inset-y-center lg:flex"
    >
      <div
        class="absolute inset-0 size-full rounded-full opacity-50 transition-all group-hover:opacity-100 group-focus:opacity-100"
      />
      <UiIcon
        name="chevron-down"
        class="size-4 -rotate-90 text-icon-secondary transition-transform group-hover:translate-x-px"
      />
    </UiButton>
    <div
      class="gallery-full-pagination absolute bottom-20 left-0 z-10 mb-2 w-full justify-center gap-4"
      :class="{ 'flex justify-center !gap-0': value.length <= 2 }"
    />
  </div>
</template>

<script lang="ts" setup>
import { Navigation, Pagination, Keyboard } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { onMounted, onUnmounted } from 'vue'
import type { Event } from '@/5_entities/Event'
import { setUserScale } from '@/6_shared/lib'
import { UiButton, UiIcon, UiPicture, UiSwiper } from '@/6_shared/ui'

type PropType = {
  eventTitle: string
  value: Event['gallery']
  initialSlide?: number
}

withDefaults(defineProps<PropType>(), {
  initialSlide: 0
})

onMounted(() => setUserScale.enable())
onUnmounted(() => setUserScale.disable())
</script>

<style scoped>
:deep(.swiper-pagination-bullet) {
  @apply size-2 md:size-3 mx-2 bg-sliderBullet rounded-full overflow-hidden relative;
}

:deep(.swiper-pagination-bullet-active) {
  @apply bg-sliderBullet-active;
}
</style>
